import { Header } from '@/components/layout/DashboardHeader';
import { Sidebar } from '@/components/layout/DashboardSidebar';
import { useMobile } from '@/hooks/useMobile';
import {
  useState,
  createContext,
  useContext,
  ReactNode,
  useEffect,
  Suspense,
} from 'react';
import { Outlet, useLocation } from 'react-router';
import { ErrorBoundary } from 'react-error-boundary';

interface DashboardLayoutProps {
  children: ReactNode;
}

interface SidebarContextType {
  isOpen: boolean;
  toggleSidebar: () => void;
}

export const SidebarContext = createContext<SidebarContextType | null>(null);

export const SidebarProvider = ({ children }: DashboardLayoutProps) => {
  const { isMobile } = useMobile();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(!isMobile);

  // Close sidebar on every route change
  useEffect(() => {
    if (isOpen) {
      setIsOpen(false);
    }
  }, [location.pathname]); // runs whenever path changes

  // Automatically close sidebar when switching to mobile
  useEffect(() => {
    if (isMobile) {
      setIsOpen(false);
    } else {
      setIsOpen(true); // Optional: reopen on desktop
    }
  }, [isMobile]);

  const toggleSidebar = () => setIsOpen((prev) => !prev);

  return (
    <SidebarContext.Provider value={{ isOpen, toggleSidebar }}>
      <div className="h-screen bg-shell">
        <Header toggleSidebar={toggleSidebar} />
        <Sidebar isOpen={isOpen} />

        <main className="lg:pl-64 ">
          <div className="h-[calc(100vh-4rem)] w-full bg-background text-foreground shadow-lg overflow-y-auto rounded-t-3xl">
            <ErrorBoundary
              fallback={<p className="p-6">⚠️Something went wrong</p>}
              resetKeys={[location.pathname]}
            >
              <Suspense fallback={<p className="p-6">⌛Loading...</p>}>
                {children}
              </Suspense>
            </ErrorBoundary>
          </div>
        </main>
      </div>
    </SidebarContext.Provider>
  );
};

export const useSidebar = () => {
  const ctx = useContext(SidebarContext);

  if (!ctx) {
    throw new Error('useSidebar must be used within a SidebarProvider.');
  }
  return ctx;
};

export const DashboardLayout = () => {
  return (
    <SidebarProvider>
      <Outlet />
    </SidebarProvider>
  );
};
